import { useState, useEffect } from 'react';
import {
    Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Paper, Typography, CircularProgress, TablePagination, Chip, IconButton, Tooltip
} from '@mui/material';
import { Visibility as VisibilityIcon, Edit as EditIcon } from '@mui/icons-material';
import { useSavedQueries } from '../../../../hooks/QueryBuilder/useSavedQueries';
import { AvailableGroup } from '../../../../context/QueryBuilderDataContext';
import EditQueryDialog from './EditQueryDialog';
import QueryResultDialog from './QueryResultDialog';
import { FilterHeader } from './FilterHeader';
import { useQueryBuilder } from '../../../../hooks/QueryBuilder/useQueryBuilder';
import { useUpdateQuery, useSaveQuery } from '../../../../hooks/QueryBuilder';
import { useQueryBuilderCache } from '../../../../context/QueryBuilderCacheContext';
import { QuerySource } from '../../../../types/QueryBuilder';
import { isAdvancedQuerySource } from '../../../../utils/queryBuilderUtils';

interface MyQueriesTabProps {
    availableGroups: AvailableGroup[];
    loadingGroups: boolean;
    onRefetchGroups: () => void;
    refreshKey?: number;
}

export function MyQueriesTab({ availableGroups, loadingGroups, onRefetchGroups, refreshKey = 0 }: MyQueriesTabProps) {
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [selectedGroupIds, setSelectedGroupIds] = useState<number[]>([]);
    const [isFromCompany, setIsFromCompany] = useState<0 | 1 | undefined>(undefined);
    const [searchTerm, setSearchTerm] = useState('');
    const [submittedSearchTerm, setSubmittedSearchTerm] = useState('');
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [selectedQuery, setSelectedQuery] = useState<Record<string, unknown> | null>(null);
    const [resultDialogOpen, setResultDialogOpen] = useState(false);
    const [resultQuery, setResultQuery] = useState<Record<string, unknown> | null>(null);
    const { fields, categories } = useQueryBuilder();
    const { updateQuery } = useUpdateQuery();
    const { saveQuery } = useSaveQuery();
    const cache = useQueryBuilderCache();

    const groupQueryIds = selectedGroupIds.length > 0 ? selectedGroupIds.join(',') : undefined;

    const { queries, totalItems, loading, refetch: refetchQueries } = useSavedQueries({
        page: page + 1,
        itemsPerPage: rowsPerPage,
        groupQueryIds,
        isFromCompany,
        search: submittedSearchTerm,
    });

    useEffect(() => {
        if (refreshKey > 0) refetchQueries();
    }, [refreshKey]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleReset = () => {
        setSelectedGroupIds([]);
        setIsFromCompany(undefined);
        setSearchTerm('');
        setSubmittedSearchTerm('');
        setPage(0);
    };

    const handleSearchSubmit = (value: string) => {
        const normalizedSearch = value.trim();
        setPage(0);
        if (normalizedSearch === submittedSearchTerm && page === 0) {
            refetchQueries();
            return;
        }
        setSubmittedSearchTerm(normalizedSearch);
    };

    const handleSaveQuery = async (updatedQuery: Record<string, unknown>, mode: 'modify' | 'copy') => {
        try {
            const source = (updatedQuery.source as QuerySource) ?? 'SIMPLE';
            if (mode === 'modify') {
                await updateQuery({
                    queryId: updatedQuery.QUERY_ID as string | number,
                    description: updatedQuery.DESCRIPTION as string,
                    groups: updatedQuery.groups as Parameters<typeof updateQuery>[0]['groups'],
                    groupId: updatedQuery.groupId as number | null,
                    source,
                });
            } else {
                await saveQuery({
                    description: updatedQuery.DESCRIPTION as string,
                    groups: updatedQuery.groups as Parameters<typeof saveQuery>[0]['groups'],
                    groupId: updatedQuery.groupId as number | null,
                    source,
                });
            }
            cache?.invalidate('saved_queries|');
            refetchQueries();
            setEditDialogOpen(false);
            setSelectedQuery(null);
        } catch {}
    };

    return (
        <Box>
            <FilterHeader
                searchTerm={searchTerm}
                onSearchChange={setSearchTerm}
                onSearchSubmit={handleSearchSubmit}
                selectedGroupIds={selectedGroupIds}
                onSelectedGroupIdsChange={(ids) => { setSelectedGroupIds(ids); setPage(0); }}
                availableGroups={availableGroups}
                loadingGroups={loadingGroups}
                isFromCompany={isFromCompany}
                onTypeChange={(value) => { setIsFromCompany(value); setPage(0); }}
                onReset={handleReset}
                totalItems={totalItems}
            />

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                    <CircularProgress />
                </Box>
            ) : queries.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 8 }}>
                    <Typography variant="body2" color="text.secondary">Aucune requête trouvée</Typography>
                </Box>
            ) : (
                <>
                    <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #e0e0e0' }}>
                        <Table sx={{ tableLayout: 'fixed', width: '100%' }}>
                            <colgroup>
                                <col style={{ width: '35%' }} />
                                <col style={{ width: '15%' }} />
                                <col style={{ width: '15%' }} />
                                <col style={{ width: '15%' }} />
                                <col style={{ width: '10%' }} />
                                <col style={{ width: '10%' }} />
                            </colgroup>
                            <TableHead>
                                <TableRow sx={{ bgcolor: '#f8f9fa' }}>
                                    <TableCell sx={{ fontWeight: 600 }}>Description</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>Groupe</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>Requêteur</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }} align="center">Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {queries.map((query) => (
                                    <TableRow key={query.QUERY_ID} hover>
                                        <TableCell sx={{ overflow: 'hidden' }}>
                                            <Typography variant="body2" noWrap title={query.DESCRIPTION || query.QUERY_NAME || ''}>{query.DESCRIPTION || query.QUERY_NAME || '-'}</Typography>
                                        </TableCell>
                                        <TableCell>
                                            {query.QUERY_GROUP_NAME && (
                                                <Chip label={query.QUERY_GROUP_NAME} size="small" sx={{ bgcolor: '#e3f2fd', color: '#1976d2' }} />
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2">
                                                {new Date(query.APPLICATION_DATE).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {new Date(query.APPLICATION_DATE).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                label={query.IS_FROM_COMPANY === '1' ? 'Enseigne' : 'Personnel'}
                                                size="small"
                                                color="primary"
                                                onClick={() => { setIsFromCompany(query.IS_FROM_COMPANY === '1' ? 1 : 0); setPage(0); }}
                                                sx={{ cursor: 'pointer' }}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                label={isAdvancedQuerySource(query.SOURCE) ? 'Avancé' : 'Simple'}
                                                size="small"
                                                sx={isAdvancedQuerySource(query.SOURCE)
                                                    ? { bgcolor: '#fff3e0', color: '#e65100' }
                                                    : { bgcolor: '#e8f5e9', color: '#2e7d32' }}
                                            />
                                        </TableCell>
                                        <TableCell align="center">
                                            <Tooltip title="Voir">
                                                <IconButton size="small" color="primary" onClick={() => { setResultQuery(query as unknown as Record<string, unknown>); setResultDialogOpen(true); }}>
                                                    <VisibilityIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Modifier">
                                                <IconButton size="small" color="secondary" onClick={() => { setSelectedQuery(query as unknown as Record<string, unknown>); setEditDialogOpen(true); }}>
                                                    <EditIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    <TablePagination
                        rowsPerPageOptions={[5, 10, 25, 50]}
                        component="div"
                        count={totalItems}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={(_, newPage) => setPage(newPage)}
                        onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                        labelRowsPerPage="Lignes par page"
                        sx={{ mt: 1 }}
                    />
                </>
            )}

            <EditQueryDialog
                open={editDialogOpen}
                onClose={() => { setEditDialogOpen(false); setSelectedQuery(null); }}
                query={selectedQuery}
                fields={fields}
                categories={categories}
                availableGroups={availableGroups}
                loadingGroups={loadingGroups}
                onRefetchGroups={onRefetchGroups}
                onSave={handleSaveQuery}
            />

            <QueryResultDialog
                open={resultDialogOpen}
                onClose={() => { setResultDialogOpen(false); setResultQuery(null); }}
                queryId={resultQuery?.QUERY_ID as string | number | null ?? null}
                description={resultQuery?.DESCRIPTION as string || resultQuery?.QUERY_NAME as string}
            />
        </Box>
    );
}

export default MyQueriesTab;
